import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarCheck, Clock3, Scissors, UsersRound, X } from "lucide-react";
import api from "../api/api";
import { sessionManager } from "../utils/sessionManager";
import { BRAND_LOGO_MARK_URL } from "../utils/runtimeConfig";

const WelcomeOnboardingModal = () => {
  const navigate = useNavigate();
  const [usuario, setUsuario] = useState(null);
  const [visible, setVisible] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!sessionManager.estaAutenticado()) return;

      try {
        const { data } = await api.get("/usuarios/me");
        if (!mounted) return;

        setUsuario(data);
        setVisible(!data?.welcome_popup_lido_em);
      } catch (error) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("[WelcomeOnboarding] Não foi possível carregar usuário:", error?.message);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!visible) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [visible]);

  const closeSessionOnly = () => {
    setVisible(false);
  };

  const navigateAndClose = (path) => {
    setVisible(false);
    navigate(path);
  };

  const markAsRead = async () => {
    setSaving(true);

    try {
      const { data } = await api.patch("/usuarios/me/welcome-lido");
      sessionManager.atualizarUsuario(data);
      setUsuario(data);
      setVisible(false);
    } catch (error) {
      console.error("Erro ao marcar boas-vindas como lida:", error);
    } finally {
      setSaving(false);
    }
  };

  if (!visible) return null;

  const primeiroNome = usuario?.nome?.split(" ").filter(Boolean)[0] || "seja bem-vindo";

  return (
    <div
      className="welcome-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          event.preventDefault();
        }
      }}
      onWheel={(event) => event.stopPropagation()}
    >
      <section className="welcome-modal" role="dialog" aria-modal="true" aria-labelledby="welcome-title">
        <button
          type="button"
          className="welcome-modal__close"
          onClick={closeSessionOnly}
          aria-label="Fechar boas-vindas"
        >
          <X size={18} />
        </button>

        <div className="welcome-modal__body">
          <div className="welcome-modal__icon">
            <img src={BRAND_LOGO_MARK_URL} alt="FilaZero" />
          </div>

          <span className="eyebrow">Bem-vindo ao FilaZero</span>
          <h2 id="welcome-title">Olá, {primeiroNome}.</h2>
          <p>
            Seu painel está pronto para centralizar agenda, equipe, serviços e atendimentos
            em um só lugar, com uma visão clara do que precisa de atenção hoje.
          </p>

          <div className="welcome-modal__features">
            <div>
              <CalendarCheck size={18} />
              <span>Revise os dados do seu estabelecimento e veja a agenda em um só lugar.</span>
            </div>
            <div>
              <UsersRound size={18} />
              <span>Cadastre os profissionais que vão aparecer para seus clientes.</span>
            </div>
            <div>
              <Scissors size={18} />
              <span>Adicione serviços com duração e preço para liberar o agendamento.</span>
            </div>
            <div>
              <Clock3 size={18} />
              <span>Configure horários e exceções antes de divulgar o link para os clientes.</span>
            </div>
          </div>

          <div className="welcome-modal__quick-actions">
            <button type="button" className="btn btn-outline" onClick={() => navigateAndClose("/configuracoes")}>
              Ver configurações
            </button>
            <button type="button" className="btn btn-outline" onClick={() => navigateAndClose("/servicos")}>
              Cadastrar serviços
            </button>
            <button type="button" className="btn btn-outline" onClick={() => navigateAndClose("/profissionais")}>
              Cadastrar profissionais
            </button>
          </div>
        </div>

        <div className="welcome-modal__actions">
          <button type="button" className="btn btn-outline" onClick={closeSessionOnly}>
            Ver depois
          </button>
          <button type="button" className="btn" onClick={markAsRead} disabled={saving}>
            {saving ? "Salvando..." : "Marcar como lido"}
          </button>
        </div>
      </section>
    </div>
  );
};

export default WelcomeOnboardingModal;
